import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Heartbeat, Pill, FirstAidKit, CalendarCheck, FileText,
  Spinner, ShieldCheck, Clock, Warning
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const Section = ({ title, icon, children, empty }) => (
  <div className="mb-5">
    <div className="flex items-center gap-2 mb-2">
      {icon}
      <h2 className="text-sm font-semibold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h2>
    </div>
    <div className="bg-white border border-slate-200 rounded-xl p-4">
      {children || <p className="text-sm text-slate-400 italic">{empty}</p>}
    </div>
  </div>
);

const SharedSummaryPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const res = await fetch(`${API}/api/share/view/${token}`);
        if (res.status === 410) {
          setError('expired');
          return;
        }
        if (!res.ok) {
          setError('invalid');
          return;
        }
        const json = await res.json();
        setData(json);
      } catch {
        setError('network');
      } finally {
        setLoading(false);
      }
    };
    fetchSummary();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <Spinner size={32} className="animate-spin text-[#235347] mx-auto mb-3" />
          <p className="text-sm text-slate-500">Loading health summary...</p>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <ShieldCheck size={48} className="text-slate-300 mx-auto mb-4" weight="duotone" />
          <h1 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>No longer shared</h1>
          <p className="text-sm text-slate-500">This health summary has been revoked by the owner and is no longer accessible.</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center px-6">
        <div className="text-center max-w-sm">
          <Warning size={48} className="text-slate-300 mx-auto mb-4" weight="duotone" />
          <h1 className="text-lg font-semibold text-slate-800 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>Link not found</h1>
          <p className="text-sm text-slate-500">This link is not valid or has expired. Please ask the person to share a new link.</p>
        </div>
      </div>
    );
  }

  const formatDate = (d) => {
    if (!d) return '';
    try {
      return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
    } catch { return d; }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="shared-summary-page">
      {/* Header */}
      <header className="bg-[#235347] text-white px-6 py-5">
        <div className="max-w-lg mx-auto">
          <p className="text-[10px] uppercase tracking-wider text-white/60 mb-1">Shared Health Summary</p>
          <h1 className="text-xl font-semibold" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {data.shared_by || 'Health Summary'}
          </h1>
          {data.label && <p className="text-sm text-white/70 mt-0.5">{data.label}</p>}
        </div>
      </header>

      <div className="max-w-lg mx-auto px-6 py-6">
        {/* Conditions */}
        <Section
          title="Current Conditions"
          icon={<Heartbeat size={16} className="text-emerald-600" weight="duotone" />}
          empty="No conditions recorded"
        >
          {data.conditions?.length > 0 && (
            <div className="space-y-2">
              {data.conditions.map((c, i) => (
                <div key={i} className="flex items-center justify-between" data-testid="shared-condition">
                  <span className="text-sm font-medium text-slate-800">{c.name}</span>
                  <div className="flex items-center gap-2">
                    {c.severity && <span className="text-xs text-slate-400">{c.severity}</span>}
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-600 font-medium">{c.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Medications */}
        <Section
          title="Current Medications"
          icon={<Pill size={16} className="text-amber-500" weight="duotone" />}
          empty="No medications recorded"
        >
          {data.medications?.length > 0 && (
            <div className="space-y-2">
              {data.medications.map((m, i) => (
                <div key={i} className="flex items-center justify-between" data-testid="shared-medication">
                  <span className="text-sm font-medium text-slate-800">{m.name}</span>
                  <span className="text-xs text-slate-400">{[m.dosage, m.frequency].filter(Boolean).join(' · ')}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Symptoms */}
        <Section
          title="Active Symptoms"
          icon={<FirstAidKit size={16} className="text-red-400" weight="duotone" />}
          empty="No symptoms recorded"
        >
          {data.symptoms?.length > 0 && (
            <div className="space-y-2">
              {data.symptoms.map((s, i) => (
                <div key={i} className="flex items-center justify-between" data-testid="shared-symptom">
                  <span className="text-sm font-medium text-slate-800">{s.name}</span>
                  <span className="text-xs text-slate-400">{[s.severity, s.frequency].filter(Boolean).join(' · ')}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Upcoming Appointments */}
        <Section
          title="Upcoming Appointments"
          icon={<CalendarCheck size={16} className="text-blue-500" weight="duotone" />}
          empty="You're clear for now — I'll flag anything new"
        >
          {data.upcoming_appointments?.length > 0 && (
            <div className="space-y-2">
              {data.upcoming_appointments.map((a, i) => (
                <div key={i} className="flex items-center justify-between" data-testid="shared-appointment">
                  <span className="text-sm font-medium text-slate-800">{a.title}</span>
                  <span className="text-xs text-slate-400">{formatDate(a.date)}{a.clinician ? ` · ${a.clinician}` : ''}</span>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Recent History */}
        <Section
          title="Recent Health History"
          icon={<Clock size={16} className="text-slate-500" weight="duotone" />}
          empty="No recent changes"
        >
          {data.recent_history?.length > 0 && (
            <div className="space-y-1.5">
              {data.recent_history.map((h, i) => (
                <div key={i} className="flex items-start gap-2" data-testid="shared-history">
                  <span className="text-[10px] text-slate-400 min-w-[60px] pt-0.5">{formatDate(h.date)}</span>
                  <div>
                    <p className="text-sm text-slate-700">{h.text}</p>
                    {h.detail && <p className="text-xs text-slate-400">{h.detail}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>

        {/* Document Findings */}
        {data.document_findings?.length > 0 && (
          <Section
            title="Document Findings"
            icon={<FileText size={16} className="text-slate-500" weight="duotone" />}
          >
            <div className="space-y-1.5">
              {data.document_findings.map((f, i) => (
                <div key={i} className="flex items-center justify-between" data-testid="shared-finding">
                  <span className="text-sm text-slate-700">{f.label}</span>
                  {f.document && <span className="text-[10px] text-slate-400">{f.document}</span>}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Disclaimer */}
        <div className="mt-6 mb-8 p-4 bg-slate-50 border border-slate-200 rounded-xl" data-testid="shared-disclaimer">
          <div className="flex gap-2">
            <ShieldCheck size={16} className="text-slate-400 flex-shrink-0 mt-0.5" weight="duotone" />
            <p className="text-xs text-slate-500 leading-relaxed">
              {data.disclaimer}
            </p>
          </div>
        </div>

        <div className="text-center pb-8">
          <p className="text-[10px] text-slate-300">Powered by Kindra Health</p>
        </div>
      </div>
    </div>
  );
};

export default SharedSummaryPage;
